import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import localeDe from '@angular/common/locales/de-AT';
import localeDeExtra from '@angular/common/locales/extra/de';
import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, LOCALE_ID, NgZone } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { ApiModule, Configuration, ConfigurationParameters } from '@core/api/v1';
import { AuthenticationInterceptor } from "@core/interceptors/authentication/authentication.interceptor";
import { ForbiddenInterceptor } from "@core/interceptors/forbidden/forbidden.interceptor";
import { CustomJsonParserHttpInterceptor } from '@core/interceptors/json/custom-json-parser-http-interceptor';
import { CustomLocalTestUserInterceptor } from '@core/interceptors/test-user/custom-local-test-user-interceptor';
import { HttpErrorService } from '@core/services/error-handler/http-error.service';
import { RuntimeErrorHandlerService } from '@core/services/error-handler/runtime-error-handler.service';
import { JSON_REPLACERS, JSON_REVIVERS, jsonDateReplacer, jsonDateReviver } from '@core/services/json/json-parser.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { NotificationService } from '@core/services/notification/notification.service';
import translation from '@core/translation/de';
import { FORM_CONTROL_ERROR_MESSAGES } from '@pva/components-lib';
import { globalErrorMessageResolver } from '@shared/utils/global-error-messages';
import { ConfirmationService, MessageService, PrimeNGConfig } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { APP_ROUTES } from 'src/app/app-routing.';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app/app.component';

export function apiConfigFactory(): Configuration {
    const params: ConfigurationParameters = {
        basePath: environment.API_URL,
    };
    return new Configuration(params);
}

const production = environment.production;
if (production) {
    enableProdMode();
}

function initializeApp(primeNGConfig: PrimeNGConfig) {
    return () => {
        primeNGConfig.setTranslation(translation.primeng);
    };
}

registerLocaleData(localeDe, 'de-AT', localeDeExtra);

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule, ApiModule.forRoot(apiConfigFactory)),
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(APP_ROUTES, withComponentInputBinding()),
        { provide: LOCALE_ID, useValue: 'de-AT' },
        { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ForbiddenInterceptor, multi: true},
        { provide: HTTP_INTERCEPTORS, useClass: CustomJsonParserHttpInterceptor, multi: true },
        ...(!production ? [{ provide: HTTP_INTERCEPTORS, useClass: CustomLocalTestUserInterceptor, multi: true }] : []),
        {
            //provider für globale validation error messages für die form-control-error-tooltip directive
            provide: FORM_CONTROL_ERROR_MESSAGES,
            useValue: [globalErrorMessageResolver],
        },
        { provide: JSON_REPLACERS, useValue: jsonDateReplacer, multi: true },
        { provide: JSON_REVIVERS, useValue: jsonDateReviver, multi: true },
        { provide: ErrorHandler, useClass: RuntimeErrorHandlerService, deps: [MessageService, LoggerService, HttpErrorService, NgZone] },
        { provide: HttpErrorService, deps: [MessageService, NotificationService, NgZone] },
        MessageService,
        ConfirmationService,
        DialogService,
        DatePipe,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [PrimeNGConfig],
            multi: true,
        },
    ],
}).catch((err) => console.error(err));
